import { For, Show } from "solid-js"
import { Markdown } from "@opencode-ai/session-ui/markdown"
import { ScrollView } from "@opencode-ai/ui/scroll-view"
import type { MarketplaceSkill } from "./skill-market-types"

/**
 * 右栏：文件内容查看器
 * - 有 readme_content 时直接展示（从 skill 对象的 readme_content 字段）
 * - 选中文件后展示文件内容，md 文件用 markdown 渲染
 */

/** 拆分 markdown 顶部的 YAML frontmatter，避免它被当成正文段落渲染 */
function splitFrontmatter(text: string): { meta: Array<[string, string]> | null; body: string } {
  if (!text.startsWith("---")) return { meta: null, body: text }
  const end = text.indexOf("\n---", 3)
  if (end === -1) return { meta: null, body: text }

  const raw = text.slice(3, end)
  const body = text.slice(end + 4).replace(/^[\r\n]+/, "")
  const meta: Array<[string, string]> = []
  for (const line of raw.split(/\r?\n/)) {
    const match = line.match(/^([A-Za-z0-9_-]+)\s*:\s*(.*)$/)
    if (!match) continue
    const value = match[2].trim().replace(/^["'](.*)["']$/, "$1")
    if (value) meta.push([match[1], value])
  }
  return { meta: meta.length > 0 ? meta : null, body }
}

function MarkdownBody(props: { text: string }) {
  const parsed = () => splitFrontmatter(props.text)
  return (
    <>
      <Show when={parsed().meta}>
        {(meta) => (
          <div class="skill-market-frontmatter">
            <For each={meta()}>
              {([key, value]) => (
                <div class="skill-market-frontmatter-row">
                  <span class="skill-market-frontmatter-key">{key}</span>
                  <span class="skill-market-frontmatter-val">{value}</span>
                </div>
              )}
            </For>
          </div>
        )}
      </Show>
      <Show when={parsed().body}>
        <Markdown text={parsed().body} class="skill-market-md" />
      </Show>
    </>
  )
}

export function SkillMarketFileViewer(props: {
  skill: MarketplaceSkill | null
  filePath: string | null
  fileContent: { path: string; content: string; is_markdown: boolean } | null
  loading: boolean
}) {
  return (
    <div class="skill-market-file-viewer-inner">
      <Show
        when={props.skill}
        fallback={<div class="skill-market-detail-empty">选择一个技能查看详情</div>}
      >
        {(skill) => (
          <>
            <Show when={props.filePath}>
              <div class="skill-market-file-viewer-header">
                <span class="skill-market-file-viewer-path">{props.filePath}</span>
              </div>
            </Show>

            <div class="skill-market-file-viewer-content-area">
              <ScrollView>
                <Show
                  when={!props.loading}
                  fallback={<div class="skill-market-file-viewer-loading">加载中...</div>}
                >
                  <Show
                    when={props.fileContent}
                    fallback={
                      <Show
                        when={skill().readme_content}
                        fallback={
                          <div class="skill-market-file-viewer-placeholder">
                            <p class="skill-market-detail-field-label">
                              {skill().display_name ?? skill().name}
                            </p>
                            <p class="skill-market-detail-field-value">
                              {skill().description ?? "暂无描述"}
                            </p>
                          </div>
                        }
                      >
                        <div class="skill-market-file-viewer-md">
                          <MarkdownBody text={skill().readme_content!} />
                        </div>
                      </Show>
                    }
                  >
                    {(fc) => (
                      <Show
                        when={fc().is_markdown}
                        fallback={
                          <pre class="skill-market-file-viewer-code">
                            <code>{fc().content}</code>
                          </pre>
                        }
                      >
                        <div class="skill-market-file-viewer-md">
                          <MarkdownBody text={fc().content} />
                        </div>
                      </Show>
                    )}
                  </Show>
                </Show>
              </ScrollView>
            </div>
          </>
        )}
      </Show>
    </div>
  )
}
